import { useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import type { UsageProfileFragment } from "@/v2-backoffice-client/usage/__types__/usage.fragment"
import {
  formatAppArea,
  formatAppCode,
  GEO_AREAS_TOOLTIP,
  USAGE_REPORT_NOTES,
} from "@/v2-backoffice-client/usage/usage.constants"
import UsageGeoAreas from "@/v2-backoffice-client/usage/UsageGeoAreas"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageHistoryStrip from "@/v2-backoffice-client/usage/UsageHistoryStrip"
import UsageActivityChart from "@/v2-backoffice-client/usage/UsageActivityChart"
import UsageAppFilterChips from "@/v2-backoffice-client/usage/UsageAppFilterChips"
import UsageProductChips from "@/v2-backoffice-client/usage/UsageProductChips"
import UsageReportBarList from "@/v2-backoffice-client/usage/report/UsageReportBarList"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportNotes from "@/v2-backoffice-client/usage/report/UsageReportNotes"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import {
  deriveAreaBreakdown,
  deriveDailyTotals,
  filterAreaDaysByApp,
  filterConsoleAreaDays,
  getAreaDayApps,
  padDailyTotals,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import { activeApps } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import { list } from "@/v2-backoffice-client/usage/usage.text"
import { deriveUserAppMix } from "@/v2-backoffice-client/user/usage/user.usage.utils"
import { USER_USAGE_HISTORY_DAYS } from "@/v2-backoffice-client/user/usage/user.usage.constants"

export type UserUsageFusionProps = {
  subject: string
  profile: UsageProfileFragment
  /** Every product's rows for the history window; the consoles are read here. */
  areaDays: UsageAreaDayRow[]
}

// The person in Fusion: console work day by day, the use cases it falls
// into and where in the country it happens. The consoles' rows alone — a
// person who only works through an MCP server must not read as a Fusion
// user — and the product filter is this tab's own state.
function UserUsageFusion(props: UserUsageFusionProps) {
  const { subject, profile, areaDays } = props
  const [appFilter, setAppFilter] = useState<string | null>(null)

  const consoleAreaDays = filterConsoleAreaDays(areaDays)
  const apps = getAreaDayApps(consoleAreaDays)

  if (consoleAreaDays.length === 0) {
    const otherProducts = activeApps(deriveUserAppMix(profile))
    return (
      <div className="usage-report">
        <UsageReportSection
          eyebrow="Fusion"
          title={`What ${subject} does in Fusion`}
          lead="Console work day by day, the use cases it falls into and where in the country it happens."
        >
          <p className="usage-report-empty">
            {subject} has not worked in Fusion in the last{" "}
            {USER_USAGE_HISTORY_DAYS} days.
            {otherProducts.length > 0
              ? ` The activity is in ${list(otherProducts.map(formatAppCode))}: the ${otherProducts.length === 1 ? "tab" : "tabs"} next to this one ${otherProducts.length === 1 ? "has" : "have"} it.`
              : ""}
          </p>
        </UsageReportSection>
      </div>
    )
  }

  const filteredAreaDays = filterAreaDaysByApp(consoleAreaDays, appFilter)
  // Zero-filled day by day, so quiet days show.
  const history = padDailyTotals(
    deriveDailyTotals(filteredAreaDays),
    USER_USAGE_HISTORY_DAYS,
  )
  const activeDays = history.filter((entry) => entry.value > 0).length
  const areaBreakdown = deriveAreaBreakdown(filteredAreaDays)

  return (
    <div className="usage-report">
      <UsageReportSection
        eyebrow="Fusion"
        title={`What ${subject} does in Fusion`}
        lead="Console work day by day, the use cases it falls into and where in the country it happens. Activity is weighted by action — exports, reports and saves count more than clicks."
        aside={<UsageProductChips apps={apps} />}
      >
        <div className="usage-report-grid usage-report-grid--two">
          {/* The chart brings its own header row (title, legend, range
              switch): no card. The strip above it shows the same days at a
              glance; the chips pick the product. */}
          <div className="col-span-full flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                {activeDays} days with activity in the last{" "}
                {USER_USAGE_HISTORY_DAYS}
              </span>
              <UsageAppFilterChips
                apps={apps}
                value={appFilter}
                onChange={setAppFilter}
              />
            </div>
            <UsageHistoryStrip entries={history} />
            <UsageActivityChart areaDays={filteredAreaDays} />
          </div>

          <UsageReportCard
            title="What they use"
            caption="Weighted activity per use case in the last 28 days, with the trend on the four weeks before."
          >
            <UsageReportBarList
              rows={areaBreakdown.map((area) => ({
                key: area.area,
                label: formatAppArea(area.area),
                value: Math.round(area.current),
                extra: (
                  <UsageTrendBadge
                    trendPct={area.trendPct}
                    className="w-16 justify-center"
                  />
                ),
              }))}
              labelWidth="w-40"
              emptyText="No use-case activity in the last 28 days."
            />
          </UsageReportCard>

          <UsageReportCard
            title="Where they work"
            info={GEO_AREAS_TOOLTIP}
            caption={
              (profile.GeoEvents28d ?? 0) > 0
                ? `Municipalities where ${formatNumber(profile.GeoEvents28d ?? 0)} located events of the last 28 days concentrate.`
                : "Municipalities where their located work concentrates."
            }
          >
            {profile.TopGeoAreas28d.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No located work yet. Municipalities appear once tracked work
                carries a property or a map position.
              </p>
            ) : (
              <UsageGeoAreas areas={profile.TopGeoAreas28d} />
            )}
          </UsageReportCard>

          <UsageReportCard wide>
            <UsageReportNotes
              notes={[
                ...USAGE_REPORT_NOTES,
                {
                  term: "Computed",
                  text: `Every night in the usage pipeline; last run ${formatDateString(profile.ComputedAt)}.`,
                },
              ]}
            />
          </UsageReportCard>
        </div>
      </UsageReportSection>
    </div>
  )
}

export default UserUsageFusion
